import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';
import { getRetailerInfo } from '../../../../actions/retailerUsersActions';

import WhatsappFlowModelMC from '../../../models/WhatsappFlowModelMC';
import EntityMetadataModelMC from '../../../models/EntityMetadataModelMC';
import TableMC from '../../../components/TableMC';
import TitleMC from '../../../components/TitleMC';
import ButtonMC from '../../../components/ButtonMC';
import LoadingMC from '../../../components/LoadingMC';
import WhatsappFlowsLandingMC from './WhatsappFlowsLandingMC';
import WhatsappFlowsBspAlertModalMC from './WhatsappFlowsBspAlertModalMC';
import WhatsappFlowsOnboardingModalMC from './WhatsappFlowsOnboardingModalMC';
import WhatsappFlowCreationModalMC from './WhatsappFlowCreationModalMC';
import WhatsappFlowTemplateModal from './WhatsappFlowTemplateModal';
import { isSyncBlocked, resolveSyncState } from '../utils/whatsappFlowsSyncUtils';
import customHistory from '../../../../customHistory';

const WhatsappFlowsListModuleMC = () => {
  const { t } = useTranslation('WhatsappFlowsScreenMC');
  const dispatch = useDispatch();
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);

  const [view, setView] = useState('loading');
  const [flows, setFlows] = useState([]);
  const [syncState, setSyncState] = useState('initial');
  const [syncing, setSyncing] = useState(false);
  const [bspModalOpen, setBspModalOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [isCreationModalVisible, setIsCreationModalVisible] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  const syncBlocked = isSyncBlocked(retailer_info);

  const loadFlows = useCallback(async () => {
    try {
      const data = await WhatsappFlowModelMC.get();
      const loadedFlows = data?.whatsapp_flows || [];
      setFlows(loadedFlows);
      setSyncState(resolveSyncState(loadedFlows, data?.last_synced_at));
      setView('loaded');
    } catch {
      setView('error');
    }
  }, []);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  useEffect(() => {
    WhatsappFlowModelMC.trackEvent('page_viewed').catch(() => {});
  }, []);

  const markOnboardingCompleted = useCallback(() => EntityMetadataModelMC.save({
    entity_metadata: {
      entity_type: 'Retailer',
      key: 'whatsapp_flows_onboarding.completed',
      value: true
    }
  }).then(() => dispatch(getRetailerInfo())), [dispatch]);

  const performSync = useCallback(async () => {
    if (syncBlocked) {
      WhatsappFlowModelMC.trackEvent('sync_blocked_bsp').catch(() => {});
      setBspModalOpen(true);
      return;
    }

    setSyncing(true);
    if (window.showtoast) window.showtoast(t('syncing_flows'));
    try {
      const data = await WhatsappFlowModelMC.sync();
      const syncedFlows = data?.whatsapp_flows || [];
      const syncedCount = data?.synced_count ?? syncedFlows.length;
      setFlows(syncedFlows);
      setSyncState(resolveSyncState(syncedFlows, data?.last_synced_at));

      if (syncedCount === 0) {
        if (window.showtoast) window.showtoast(t('landing.sync_empty.toast'));
      } else if (window.showtoast) {
        window.showtoast(t('flows_synced_success'));
      }
    } catch (e) {
      console.error('Sync error', e);
      if (window.showtoast) window.showtoast(t('flows_sync_error'));
    } finally {
      setSyncing(false);
    }
  }, [syncBlocked, t]);

  const handleSync = () => {
    if (retailer_info?.whatsapp_flows_onboarding_completed !== true) {
      setOnboardingOpen(true);
      return;
    }
    performSync();
  };

  const handleOnboardingClose = () => {
    markOnboardingCompleted()
      .catch((error) => {
        if (window.showtoast) window.showtoast(error.message);
      })
      .finally(() => setOnboardingOpen(false));
  };

  const handleOnboardingFinish = () => {
    markOnboardingCompleted()
      .catch((error) => {
        if (window.showtoast) window.showtoast(error.message);
      })
      .finally(() => {
        setOnboardingOpen(false);
        performSync();
      });
  };

  const handleOpenCreate = () => {
    setIsCreationModalVisible(true);
  };

  const handleOpenTemplates = () => {
    setIsCreationModalVisible(false);
    setShowTemplatesModal(true);
  };

  const handleViewDetail = (flow) => {
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'WhatsappFlowDetailModal',
        props: { flow, className: 'tw-w-[75vw] tw-max-w-[1200px]' },
        closeOnOverlayClick: false
      }
    });
  };

  const handleOpenResponses = (flow) => {
    customHistory.push(`/retailers/${ENV.SLUG}/whatsapp_flows/${flow.meta_flow_id}/responses`);
  };

  const handleOpenConfig = (flow) => {
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'WhatsappFlowMappingModal',
        props: {
          flow,
          onSaved: (updatedFlow) => {
            setFlows((prevFlows) =>
              prevFlows.map((f) => {
                const isMatch = f.id === (updatedFlow?.id || flow.id) ||
                  f.meta_flow_id === (updatedFlow?.meta_flow_id || flow.meta_flow_id);
                return isMatch ? { ...f, ...flow, ...(updatedFlow || {}) } : f;
              })
            );
          },
          className: 'tw-w-[50vw] tw-max-w-[800px]'
        },
        closeOnOverlayClick: false
      }
    });
  };

  const createAction = (action, flow) => {
    switch (action) {
      case 'view_detail':
        return {
          icon: 'eye-outline',
          label: t('view_detail'),
          action: () => handleViewDetail(flow)
        };
      case 'responses':
        return {
          icon: 'message-square-outline',
          label: t('responses_tab'),
          action: () => handleOpenResponses(flow)
        };
      case 'configure':
        return {
          icon: 'settings-outline',
          label: t('configure'),
          action: () => handleOpenConfig(flow)
        };
      default:
        return null;
    }
  };

  const headers = [
    { key: 'name', label: t('name_label') },
    { key: 'title', label: t('title_label') },
    { key: 'metaFlowId', label: t('meta_flow_id_label') },
    { key: 'categories', label: t('categories_label') },
    { key: 'status', label: t('status') },
    { key: 'syncedAt', label: t('synced_at_label') }
  ];

  const getFlowRows = () => flows.map((flow) => ({
    id: flow.id,
    name: { value: flow.name, type: 'text' },
    title: { value: flow.default_header_text || '-', type: 'text' },
    metaFlowId: { value: flow.meta_flow_id, type: 'text' },
    categories: { value: flow.categories?.join(', '), type: 'text' },
    status: { value: flow.status, type: 'status-label' },
    syncedAt: { value: flow.synced_at ? new Date(flow.synced_at).toLocaleString() : '-', type: 'text' },
    actions: [
      createAction('view_detail', flow),
      createAction('responses', flow),
      createAction('configure', flow)
    ]
  }));

  return (
    <div className="tw-wrapper tw-p-4">
      <WhatsappFlowsBspAlertModalMC
        visible={bspModalOpen}
        onClose={() => setBspModalOpen(false)}
      />
      <WhatsappFlowsOnboardingModalMC
        visible={onboardingOpen}
        onClose={handleOnboardingClose}
        onFinish={handleOnboardingFinish}
      />
      <WhatsappFlowCreationModalMC
        visible={isCreationModalVisible}
        onClose={() => setIsCreationModalVisible(false)}
        onPressCreateFromTemplate={handleOpenTemplates}
      />
      <WhatsappFlowTemplateModal
        visible={showTemplatesModal}
        onClose={() => setShowTemplatesModal(false)}
        onCloseCreationModal={() => setIsCreationModalVisible(false)}
        onSync={performSync}
      />
      {view === 'error' ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-py-20">
          <p className="tw-text-red-500">{t('error_loading')}</p>
        </div>
      ) : view === 'loading' ? (
        <LoadingMC loading className="tw-py-20" />
      ) : (
        <>
          <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-mb-5">
            <TitleMC
              text={t('title')}
              variant="H5"
              textClassName="tw-font-semibold"
              className="!tw-gap-1"
            />
            <div className="tw-flex tw-gap-2">
              <ButtonMC
                text={t('create_flow')}
                onClick={handleOpenCreate}
              />
              <ButtonMC
                text={t('sync_flows')}
                onClick={handleSync}
                loading={syncing}
                variant="SECONDARY"
                outlined
              />
            </div>
          </div>

          {flows.length === 0 ? (
            <WhatsappFlowsLandingMC onSync={handleSync} syncing={syncing} syncState={syncState} onCreate={handleOpenCreate} />
          ) : (
            <TableMC
              headers={headers}
              data={getFlowRows()}
              borderAll
            />
          )}
        </>
      )}
    </div>
  );
};

export default WhatsappFlowsListModuleMC;
